import type { UpcomingItem } from '../../api/upcoming.service';
import type { DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function toUpcoming(
  session: import('../../api/class-session.service').ClassSession,
): UpcomingItem {
  const startDateTime = `${session.date}T${session.startTime}:00`;
  const endDateTime = `${session.date}T${session.endTime}:00`;
  const now = new Date();
  return {
    itemKind: 'ClassSession',
    id: session.sessionId,
    title: session.title ?? 'Antrenament',
    startDateTime,
    endDateTime,
    locationId: session.locationId,
    status: session.status,
    isOngoing:
      new Date(startDateTime).getTime() <= now.getTime() &&
      now.getTime() <= new Date(endDateTime).getTime(),
    classSession: session,
  };
}

function sessionsInRange(
  data: DemoStoreData,
  from: string | null,
  to: string | null,
): readonly import('../../api/class-session.service').ClassSession[] {
  return data.sessions.filter((s) => {
    if (from && s.date < from) return false;
    if (to && s.date > to) return false;
    return true;
  });
}

export const upcomingCalendarHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/upcoming$/, (ctx, data) => {
    const from = ctx.query.get('from');
    const to = ctx.query.get('to');
    return sessionsInRange(data, from, to)
      .filter((s) => s.status !== 'Canceled')
      .map(toUpcoming)
      .sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));
  }),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/calendar\/coach\/([^/]+)$/,
    (ctx, data) => {
      const coachId = decodeURIComponent(ctx.match[2]);
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      return sessionsInRange(data, from, to).filter((s) =>
        s.coachTenantAccountIds.includes(coachId),
      );
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/calendar\/student\/([^/]+)$/,
    (ctx, data) => {
      const studentId = decodeURIComponent(ctx.match[2]);
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      const studentGroups = new Set(
        data.enrollments
          .filter((e) => e.studentTenantAccountId === studentId && e.status === 'Active')
          .map((e) => e.groupId),
      );
      return sessionsInRange(data, from, to).filter(
        (s) => s.groupId !== null && studentGroups.has(s.groupId),
      );
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/calendar\/location\/([^/]+)$/,
    (ctx, data) => {
      const locationId = decodeURIComponent(ctx.match[2]);
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      return sessionsInRange(data, from, to).filter((s) => s.locationId === locationId);
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/attendance$/,
    (ctx, data) => {
      const studentId = decodeURIComponent(ctx.match[2]);
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      return buildStudentAttendance(data, studentId, from, to);
    },
  ),
  route('GET', /^\/api\/tenants\/([^/]+)\/me\/attendance$/, (ctx, data) => {
    const from = ctx.query.get('from');
    const to = ctx.query.get('to');
    return buildStudentAttendance(data, data.currentMemberId, from, to);
  }),
];

function buildStudentAttendance(
  data: DemoStoreData,
  studentId: string,
  from: string | null,
  to: string | null,
) {
  return data.attendances
    .filter((a) => a.studentTenantAccountId === studentId)
    .map((a) => {
      const session = data.sessions.find((s) => s.sessionId === a.classSessionId);
      if (!session) return null;
      if (from && session.date < from) return null;
      if (to && session.date > to) return null;
      const group = session.groupId
        ? data.groups.find((g) => g.groupId === session.groupId)
        : null;
      return {
        attendanceId: a.attendanceId,
        classSessionId: a.classSessionId,
        sessionDate: session.date,
        sessionStartTime: session.startTime,
        sessionEndTime: session.endTime,
        sessionTitle: session.title,
        sessionStatus: session.status,
        groupId: session.groupId,
        groupName: group?.name ?? null,
        locationId: session.locationId,
        status: a.status,
        isGuest: a.isGuest,
        notes: a.notes,
        checkedInAt: a.checkedInAt,
        updatedAt: a.updatedAt,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
}
